import React, { ReactNode } from 'react';
import {
  CalendarOutlined,
  CrownOutlined,
  DesktopOutlined,
  DollarOutlined,
  HomeOutlined,
  PlayCircleOutlined,
  QuestionCircleOutlined,
  SettingOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useLocation } from 'react-router-dom';
import { Flex } from 'antd';
import { useTranslation } from 'react-i18next';

const HeaderWithIcon = ({ icon, text }: { icon: ReactNode; text: string }) => {
  return (
    <Flex align={'center'} justify={'center'}>
      <span style={{ fontSize: '24px', color: '#1890ff', marginRight: '15px' }}>
        {icon}
      </span>
      <span>{text}</span>
    </Flex>
  );
};

export interface LayoutItem {
  key: string;
  icon?: ReactNode;
  disableClick?: boolean;
  label?: string | ReactNode;
  hidden?: boolean;
  hideHeader?: boolean;
  createButton?: {
    path: string;
  };
  children?: LayoutItem[];
}

const getRouteKey = (pathname: string) => {
  const segments = pathname
    .split('/')
    .filter((segment) => segment && segment !== 'new');

  if (segments.length === 0) {
    return ['videos'];
  }

  return segments;
};

const useCurrentPage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const currentPage = getRouteKey(location.pathname);
  const firstPage = currentPage[0];

  const layoutItems: LayoutItem[] = [
    {
      key: 'home',
      icon: <HomeOutlined />,
      label: t('nav.home'),
      hideHeader: true,
    },
    {
      key: 'series',
      icon: <DesktopOutlined />,
      label: t('nav.series'),
      createButton: {
        path: 'series/form/new',
      },
    },
    {
      key: 'videos',
      icon: <PlayCircleOutlined />,
      label: t('nav.videos'),
      createButton: {
        path: 'videos/form/new',
      },
    },
    {
      key: 'calendar',
      icon: <CalendarOutlined />,
      label: t('nav.calendar'),
    },
    {
      key: 'series-form',
      label: t('nav.series'),
      hidden: true,
    },
    {
      key: 'videos-form',
      label: (
        <HeaderWithIcon icon={<PlayCircleOutlined />} text={t('nav.create')} />
      ),
      hidden: true,
    },
    {
      key: 'credits',
      icon: <CrownOutlined />,
      label: t('nav.credits'),
      hideHeader: true,
    },
    {
      key: 'social-accounts',
      icon: <UserOutlined />,
      label: t('nav.socialAccounts'),
      hideHeader: true,
    },
    {
      key: 'affiliate',
      icon: <DollarOutlined />,
      label: t('nav.affiliate'),
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: t('nav.settings'),
    },
    {
      key: 'help',
      icon: <QuestionCircleOutlined />,
      label: t('nav.help'),
    },
    {
      key: 'settings-profile',
      icon: <SettingOutlined />,
      label: t('nav.settings'),
      hidden: true,
    },
    {
      key: 'settings-billing',
      icon: <SettingOutlined />,
      label: t('nav.settings'),
      hidden: true,
    },
    {
      key: 'settings-social-accounts',
      icon: <SettingOutlined />,
      label: t('nav.settings'),
      hidden: true,
    },
    {
      key: 'settings-email-notifications',
      icon: <SettingOutlined />,
      label: t('nav.settings'),
      hidden: true,
    },
  ];

  const menuItem = layoutItems.find((item) => {
    const pathItems = item.key.split('-');

    if (pathItems.length < currentPage.length) {
      return false;
    }

    return item.key === currentPage.join('-');
  });

  const firstPageTitle = menuItem?.label || t('notFound.title');

  return {
    currentPage,
    isSinglePage: currentPage.length === 1,
    firstPage,
    firstPageTitle,
    menuItem,
    layoutItems,
  };
};

export { useCurrentPage };
